/**
 * Fail-Safe LocalStorage & SessionStorage utilities with automatic QuotaExceededError handling,
 * data URL sanitization, and fallback mechanisms.
 */

const NON_ESSENTIAL_CACHE_KEYS = [
  'site_diagnostic_issues_cache',
  'last_site_diagnostic_scan',
  'he_instruments_cache',
  'he_tracking_notification_events',
  'he_admin_shipment_history',
  'hamim_settings_v1'
];

/**
 * Remove oversized base64 data URLs from objects before storing in localStorage cache.
 */
export function sanitizeForCache<T = any>(data: T): T {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeForCache(item)) as unknown as T;
  }

  const clean: any = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      // Never drop product images (imageUrl, image2Url, image3Url)
      if (key === 'imageUrl' || key === 'image2Url' || key === 'image3Url') {
        clean[key] = value;
      } else if (value.startsWith('data:') && value.length > 100000) {
        // Only skip non-essential giant data URLs (>100KB)
        continue;
      } else {
        clean[key] = value;
      }
    } else if (value && typeof value === 'object') {
      clean[key] = sanitizeForCache(value);
    } else {
      clean[key] = value;
    }
  }
  return clean as T;
}

/**
 * Free up storage space when quota is approached or exceeded.
 */
export function pruneStorage() {
  if (typeof window === 'undefined') return;
  try {
    for (const key of NON_ESSENTIAL_CACHE_KEYS) {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn('[Storage] Prune non-essential cache notice:', e);
  }
}

export const safeStorage = {
  getItem<T = any>(key: string, fallback: T): T {
    if (typeof window === 'undefined') return fallback;
    try {
      const item = localStorage.getItem(key);
      if (item === null || item === undefined) return fallback;
      try {
        return JSON.parse(item) as T;
      } catch {
        return item as unknown as T;
      }
    } catch (e) {
      console.warn(`[Storage] Failed to get item "${key}":`, e);
      return fallback;
    }
  },

  setItem(key: string, value: any, options: { sanitize?: boolean } = { sanitize: true }): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const valueToStore = options.sanitize && typeof value === 'object' 
        ? sanitizeForCache(value) 
        : value;

      const serialized = typeof valueToStore === 'string' 
        ? valueToStore 
        : JSON.stringify(valueToStore);

      localStorage.setItem(key, serialized);
      return true;
    } catch (error: any) {
      console.warn(`[Storage] Error setting "${key}", attempting recovery...`, error);
      // Quota exceeded recovery
      pruneStorage();
      try {
        // Try storing a strictly sanitized version
        const deeplySanitized = typeof value === 'object' ? sanitizeForCache(value) : value;
        const serialized = typeof deeplySanitized === 'string' ? deeplySanitized : JSON.stringify(deeplySanitized);
        localStorage.setItem(key, serialized);
        return true;
      } catch (retryError) {
        console.warn(`[Storage] Failed to store "${key}" after storage cleanup (suppressed to prevent crash):`, retryError);
        return false;
      }
    }
  },

  removeItem(key: string): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`[Storage] Failed to remove "${key}":`, e);
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.clear();
    } catch (e) {
      console.warn('[Storage] Failed to clear localStorage:', e);
    }
  }
};

export const safeSessionStorage = {
  getItem<T = any>(key: string, fallback: T): T {
    if (typeof window === 'undefined') return fallback;
    try {
      const item = sessionStorage.getItem(key);
      if (item === null || item === undefined) return fallback;
      try {
        return JSON.parse(item) as T;
      } catch {
        return item as unknown as T;
      }
    } catch (e) {
      console.warn(`[SessionStorage] Failed to get item "${key}":`, e);
      return fallback;
    }
  },

  setItem(key: string, value: any): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      sessionStorage.setItem(key, serialized);
      return true;
    } catch (e) {
      console.warn(`[SessionStorage] Failed to set "${key}":`, e);
      try {
        sessionStorage.removeItem(key);
      } catch {}
      return false;
    }
  },

  removeItem(key: string): void {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.warn(`[SessionStorage] Failed to remove "${key}":`, e);
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.clear();
    } catch (e) {
      console.warn('[SessionStorage] Failed to clear sessionStorage:', e);
    }
  }
};
